import { fetchNavigationalSearchV2 } from '#pplx/frontend/apps/ask/queries/navigational-queries/fetchNavigationalSearchV2.js';
import { fetchNavigationalSearchV3 } from '#pplx/frontend/apps/ask/queries/navigational-queries/fetchNavigationalSearchV3.js';
import { getNavigationClassifierQueryKey, getNavigationRequestPendingQueryKey, getNavigationResultsQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/navigationKeys.js';
import { NavigationSource } from '#pplx/frontend/components/types/types.js';
import { logger } from '#pplx/frontend/logger/browser.js';
export const runWebNavigationalSearch = async ({ query, userIdentity, queryClient, reason, streamTimer, isComet, streamId, }) => {
    const requestPendingQueryKey = !isComet && streamId ? getNavigationRequestPendingQueryKey(streamId) : undefined;
    if (requestPendingQueryKey) {
        queryClient.setQueryData(requestPendingQueryKey, true);
    }
    try {
        streamTimer?.addTimingOnce('web.frontend.web_v3_search_request_sent_ms');
        // The cache key is query-only, but Comet (v3) and non-Comet (v2) hit different
        // endpoints, so only reuse an entry written by the current mode's endpoint.
        const expectedSource = isComet
            ? NavigationSource.SEARCH_V3_NAVIGATE
            : NavigationSource.SEARCH_V2_NAVIGATE;
        const cachedData = queryClient.getQueryData(getNavigationResultsQueryKey(query));
        if (cachedData &&
            cachedData.length > 0 &&
            cachedData[0]?.navigation_source === expectedSource) {
            streamTimer?.addTimingOnce('web.frontend.web_v3_search_results_received_ms', {
                count: cachedData.length,
                from_cache: true,
            });
            return;
        }
        let navigationResults;
        if (isComet) {
            const navResponse = await fetchNavigationalSearchV3({
                query,
                reason,
                streamId,
                streamTimer,
                provider: 'v3',
                userIdentity: {
                    lang: userIdentity.lang,
                    country: userIdentity.country,
                },
            });
            const classifier = navResponse?.classifier;
            // An empty classifier record would shadow real entry data downstream;
            // useClassifierResults also guards reads (hasClassifierData).
            if (streamId && classifier && Object.keys(classifier).length > 0) {
                queryClient.setQueryData(getNavigationClassifierQueryKey(streamId), classifier);
            }
            navigationResults = navResponse?.hits?.map((hit) => ({
                url: hit.url,
                title: hit.title ?? '',
                snippet: hit.snippet ?? '',
                domain: hit.domain ?? undefined,
                score: hit.score ?? undefined,
                sitelinks: hit.sitelinks,
                trust: hit.trust,
                navigation_source: NavigationSource.SEARCH_V3_NAVIGATE,
            }));
        }
        else {
            const navResponse = await fetchNavigationalSearchV2({
                query,
                reason,
                streamId,
                streamTimer,
                userIdentity: {
                    lang: userIdentity.lang,
                    country: userIdentity.country,
                },
            });
            const navHits = navResponse?.hit?.navigation_results;
            if (!navHits?.length) {
                logger.info('[navigation] /search/v2/navigate returned no results', {
                    stream_id: streamId,
                });
            }
            // v2 returns no classifier (SAPI filters non-nav queries server-side). Write a
            // minimal non-hiding classifier so the widget renders as early as the v3 path,
            // instead of waiting for the streamed thread classifier.
            if (streamId && navHits && navHits.length > 0) {
                queryClient.setQueryData(getNavigationClassifierQueryKey(streamId), {
                    mhe_predictions: {},
                });
            }
            navigationResults = navHits?.map((result) => ({
                url: result.url,
                title: result.title ?? '',
                snippet: result.snippet ?? '',
                domain: result.domain ?? undefined,
                score: result.score ?? undefined,
                sitelinks: result.sitelinks ?? undefined,
                trust: result.trust,
                navigation_source: NavigationSource.SEARCH_V2_NAVIGATE,
            }));
        }
        if (navigationResults && navigationResults.length > 0) {
            // Fire metric before updating cache to ensure proper ordering with render events
            // addTimingOnce prevents duplicate if cache hit already fired above
            streamTimer?.addTimingOnce('web.frontend.web_v3_search_results_received_ms', {
                count: navigationResults.length,
                from_cache: false,
            });
            queryClient.setQueryData(getNavigationResultsQueryKey(query), navigationResults);
        }
    }
    catch (error) {
        logger.error('Failed to fetch navigational search results:', error);
    }
    finally {
        if (requestPendingQueryKey) {
            queryClient.setQueryData(requestPendingQueryKey, false);
        }
    }
};
//# sourceMappingURL=runWebNavigationalSearch.js.map